/**
 * Recently viewed products, stored in the shopper's browser (localStorage).
 *
 * <s-recently-viewed-tracker data-product='{"handle","title","url","image","hover_image","price"}'>
 *   Placed on product pages; records the product as the most recent view.
 *
 * <s-recently-viewed data-limit="24" hidden>
 *   Renders stored products into a 4x2 paged grid (8 per page) with page-flipping
 *   controls and unhides itself when there is at least one product.
 */
const STORAGE_KEY = 'towdio:recently-viewed';
const MAX_STORED = 24;
const ITEMS_PER_PAGE = 8;

function readItems() {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(items) ? items.filter((item) => item && item.handle && item.url) : [];
  } catch {
    return [];
  }
}

function writeItems(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_STORED)));
  } catch {
    /* Storage can be unavailable (private mode); fail silently. */
  }
}

class SRecentlyViewedTracker extends HTMLElement {
  connectedCallback() {
    let product;
    try {
      product = JSON.parse(this.dataset.product || 'null');
    } catch {
      return;
    }
    if (!product || !product.handle) return;

    const items = readItems().filter((item) => item.handle !== product.handle);
    items.unshift(product);
    writeItems(items);
  }
}

class SRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-recent-track]');
    this.controls = this.querySelector('[data-recent-controls]');
    this.prevBtn = this.querySelector('[data-recent-prev]');
    this.nextBtn = this.querySelector('[data-recent-next]');
    this.paginationText = this.querySelector('[data-recent-pagination]');

    if (!this.track) return;

    const limit = Number(this.dataset.limit) || MAX_STORED;
    let items = readItems().slice(0, limit);

    // In designMode (Canvas theme editor), fallback to sample products if localStorage is empty
    if (items.length === 0 && window.Shopify && window.Shopify.designMode) {
      const sampleScript = this.querySelector('[data-sample-products]');
      if (sampleScript) {
        try {
          const sampleData = JSON.parse(sampleScript.textContent || '[]');
          if (Array.isArray(sampleData) && sampleData.length > 0) {
            items = sampleData.slice(0, limit);
          }
        } catch {}
      }
    }

    if (items.length === 0) {
      this.setAttribute('hidden', '');
      return;
    }

    this.renderPages(items);
    this.removeAttribute('hidden');

    this.onPrev = () => this.flipPage(-1);
    this.onNext = () => this.flipPage(1);
    this.onScroll = () => this.updateControls();

    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  renderPages(items) {
    this.track.replaceChildren();

    const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
    this.totalPages = totalPages;

    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
      const pageItems = items.slice(pageIdx * ITEMS_PER_PAGE, (pageIdx + 1) * ITEMS_PER_PAGE);
      const pageList = document.createElement('ul');
      pageList.setAttribute('role', 'list');
      pageList.className = 'w-full shrink-0 snap-start grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8';

      for (const item of pageItems) {
        pageList.append(this.#card(item));
      }

      this.track.append(pageList);
    }
  }

  #card(item) {
    const li = document.createElement('li');

    const link = document.createElement('a');
    link.href = item.url;
    link.className = 'group/card flex flex-col gap-2 text-on-surface no-underline';
    link.setAttribute('aria-label', item.title || '');

    const media = document.createElement('div');
    media.className = 'relative aspect-square overflow-hidden rounded-card ring-2 ring-on-surface bg-surface-secondary';

    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = item.title || '';
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      img.className = 'size-full object-cover';
      media.append(img);
    }

    if (item.hover_image) {
      const hoverImg = document.createElement('img');
      hoverImg.src = item.hover_image;
      hoverImg.alt = '';
      hoverImg.loading = 'lazy';
      hoverImg.setAttribute('aria-hidden', 'true');
      hoverImg.width = 600;
      hoverImg.height = 600;
      hoverImg.className = 'absolute inset-0 size-full object-cover opacity-0 motion-safe:transition-opacity motion-safe:duration-300 [@media(hover:hover)]:group-hover/card:opacity-100';
      media.append(hoverImg);
    }

    const title = document.createElement('h3');
    title.className = 'type-body-sm font-medium text-on-surface line-clamp-2 mt-1';
    title.textContent = item.title || '';

    link.append(media, title);

    if (item.price) {
      const price = document.createElement('span');
      price.className = 'text-sm font-bold text-on-surface';
      price.textContent = item.price;
      link.append(price);
    }

    li.append(link);
    return li;
  }

  flipPage(direction) {
    const pageWidth = this.track.clientWidth;
    this.track.scrollBy({ left: pageWidth * direction, behavior: 'smooth' });
  }

  updateControls() {
    if (!this.controls || !this.totalPages) return;

    if (this.totalPages <= 1) {
      this.controls.hidden = true;
      return;
    }

    this.controls.hidden = false;

    const pageWidth = this.track.clientWidth;
    if (pageWidth <= 0) return;

    const currentPage = Math.min(
      this.totalPages,
      Math.max(1, Math.round(this.track.scrollLeft / pageWidth) + 1)
    );

    if (this.paginationText) {
      this.paginationText.textContent = `${currentPage} / ${this.totalPages}`;
    }

    if (this.prevBtn) {
      this.prevBtn.disabled = currentPage <= 1;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = currentPage >= this.totalPages;
    }
  }
}

if (!customElements.get('s-recently-viewed-tracker')) {
  customElements.define('s-recently-viewed-tracker', SRecentlyViewedTracker);
}
if (!customElements.get('s-recently-viewed')) {
  customElements.define('s-recently-viewed', SRecentlyViewed);
}
